import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { fetchAuthSession, signOut as amplifySignOut } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { clearSession, getJWTToken, hasInsightSession } from '../utils/auth';
import { logEvent } from '../utils/eventLog';
import { claimDistrictCaches } from './cacheOwner';
import { followHandoffImitation } from './handoffImitation';
import {
  HANDOFF_CHECK_MS,
  bootstrapHandoffFromUrl,
  clearHandoff,
  forgetHandoffTab,
  getHandoffToken,
  handoffImitating,
  handoffSubject,
} from './preauth';
import {
  getSessionSource,
  notifySessionChange,
  type SessionSource,
  setSessionSource,
  subscribeSessionChange,
} from './sessionState';
import { clearPlansStatusCache } from '../pages/Plans/plansGenerated';
import { resetSisMappingSession } from '../pages/Data/sisMappingApi';
import { invalidateStagedRosterRebuilds } from '../pages/Upload/uploadRehydrate';
import { hydrateThresholds, resetThresholdsSession } from './thresholdsApi';
import { saveReclassificationSelection } from './reclassificationSelection';
import { STATE_CRITERIA } from '../pages/Reclassification/stateCriteria';
import { hydrateSchoolYears, resetSchoolYearsSession } from './schoolYearsApi';
import { resetProgressReportsSession } from '../pages/ProgressReports/students';
import { invalidateRosterHydration } from '../pages/Data/uploadedFiles';
import { invalidateProgramEvaluation } from '../pages/ProgramEvaluation/program-evaluation-hook';
import {
  HANDOFF_EVENT_KEY,
  abandonImpersonation,
  authHeaders,
  getImpersonation,
  installBackForwardGuard,
  installCrossTabSync,
  registerImpersonationCleanup,
  type ImpersonationTarget,
} from './impersonation';

export type BambooUser = {
  id: string;
  first_name: string | null;
};

export type BambooDistrict = {
  name: string | null;
  state: string | null;
  stateName: string | null;
};

export type BambooAccess = {
  hasAccess: boolean;
  userId: string | null;
  districtName: string | null;
  districtState: string | null;
  districtStateName: string | null;
  isSuperAdmin: boolean;
  isDistrictAdmin: boolean;
  impersonating: boolean;
  features: Record<string, boolean>;
};

function parseFeatures(raw: unknown): Record<string, boolean> {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return {};
  const features: Record<string, boolean> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (typeof value === 'boolean') features[key] = value;
  }
  return features;
}

type AuthContextValue = {
  isInitializing: boolean;
  isAuthenticated: boolean;
  user: BambooUser | null;
  hasBambooAccess: boolean | null;
  district: BambooDistrict | null;
  isSuperAdmin: boolean;
  isDistrictAdmin: boolean;
  hasFeature: (key: string) => boolean;
  impersonation: ImpersonationTarget | null;
  refreshUser: () => Promise<void>;
  signOut: () => Promise<void>;
};

type ResolvedSession = { source: SessionSource; sub: string | null };

const NO_SESSION: ResolvedSession = { source: null, sub: null };

async function resolveSessionSource(): Promise<ResolvedSession> {
  if (getHandoffToken()) return { source: 'handoff', sub: handoffSubject() };
  if (!(await hasInsightSession())) return NO_SESSION;
  try {
    const { tokens } = await fetchAuthSession();
    if (!tokens) return NO_SESSION;
    const sub = tokens.idToken?.payload?.sub;
    return { source: 'amplify', sub: typeof sub === 'string' ? sub : null };
  } catch {
    return { source: 'amplify', sub: null };
  }
}

function clearDistrictCaches(): void {
  clearPlansStatusCache();
  resetSisMappingSession();
  resetThresholdsSession();
  resetSchoolYearsSession();
  resetProgressReportsSession();
  invalidateStagedRosterRebuilds();
  invalidateRosterHydration();
  invalidateProgramEvaluation();
}

let handoffEventInFlightFor: number | null = null;

function recordHandoffSignIn(epoch: number, isCurrent: () => boolean): void {
  try {
    if (sessionStorage.getItem(HANDOFF_EVENT_KEY) === '1') return;
  } catch {
    return;
  }
  if (handoffEventInFlightFor === epoch) return;
  handoffEventInFlightFor = epoch;
  void logEvent('user.handoff.signed_in.v1', undefined, { asActor: true })
    .then(() => {
      if (!isCurrent()) return;
      try {
        sessionStorage.setItem(HANDOFF_EVENT_KEY, '1');
      } catch {
        return;
      }
    })
    .catch(() => undefined)
    .finally(() => {
      if (handoffEventInFlightFor === epoch) handoffEventInFlightFor = null;
    });
}

type RefreshOptions = { follow?: string | null; failClosed?: boolean };

function impersonationMatches(email: string): boolean {
  const stored = getImpersonation();
  return stored !== null && stored.email.toLowerCase() === email.toLowerCase();
}

// A refused imitated handoff must not fall through to whatever Insight login
// the browser still holds, now or on the next reload.
function refuseHandoff(): void {
  clearHandoff();
  clearSession();
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const bambooApiUrl = import.meta.env.REACT_APP_BAMBOO_API_URL ?? '';

const NO_ACCESS: BambooAccess = {
  hasAccess: false,
  userId: null,
  districtName: null,
  districtState: null,
  districtStateName: null,
  isSuperAdmin: false,
  isDistrictAdmin: false,
  impersonating: false,
  features: {},
};

type AccessAttempt = { access: BambooAccess; refused: boolean };

async function attemptBambooAccess(): Promise<AccessAttempt> {
  if (!bambooApiUrl) return { access: NO_ACCESS, refused: false };
  try {
    const token = await getJWTToken();
    const response = await fetch(`${bambooApiUrl}/access`, {
      method: 'GET',
      headers: authHeaders(token),
    });
    if (!response.ok) {
      return { access: NO_ACCESS, refused: response.status === 401 || response.status === 403 };
    }
    const payload = (await response.json()) as {
      hasAccess?: boolean;
      userId?: string;
      districtName?: string;
      districtState?: string;
      districtStateName?: string;
      isSuperAdmin?: boolean;
      isDistrictAdmin?: boolean;
      impersonating?: boolean;
      features?: unknown;
    };
    return {
      refused: false,
      access: {
        hasAccess: Boolean(payload?.hasAccess),
        userId: typeof payload?.userId === 'string' && payload.userId !== '' ? payload.userId : null,
        districtName: payload?.districtName ?? null,
        districtState: payload?.districtState ?? null,
        districtStateName: payload?.districtStateName ?? null,
        isSuperAdmin: Boolean(payload?.isSuperAdmin),
        isDistrictAdmin: Boolean(payload?.isDistrictAdmin),
        impersonating: payload?.impersonating === true,
        features: parseFeatures(payload?.features),
      },
    };
  } catch {
    return { access: NO_ACCESS, refused: false };
  }
}

function rememberPlanCriteria(districtStateName: string | null): void {
  const entry = STATE_CRITERIA.find((c) => c.state === districtStateName);
  if (!entry) return;
  if (!saveReclassificationSelection({ state: entry.state, assessment: entry.assessment })) {
    console.warn('plan criteria selection not written; this browser renders default criteria');
  }
}

const retryDelay = () => new Promise((resolve) => setTimeout(resolve, 3000));

async function fetchBambooAccessOnce(): Promise<BambooAccess> {
  return (await attemptBambooAccess()).access;
}

export async function fetchBambooAccess(): Promise<BambooAccess> {
  const first = await fetchBambooAccessOnce();
  if (first.hasAccess) return first;
  await retryDelay();
  return fetchBambooAccessOnce();
}

export async function fetchAccessForSession(): Promise<{
  access: BambooAccess;
  impersonation: ImpersonationTarget | null;
}> {
  const stored = getImpersonation();
  if (!stored) return { access: await fetchBambooAccess(), impersonation: null };
  let attempt = await attemptBambooAccess();
  if (!attempt.access.hasAccess && !attempt.refused) {
    await retryDelay();
    attempt = await attemptBambooAccess();
  }
  if (attempt.access.hasAccess && attempt.access.impersonating) {
    return { access: attempt.access, impersonation: stored };
  }
  if (attempt.refused || attempt.access.hasAccess) {
    abandonImpersonation();
    return { access: await fetchBambooAccess(), impersonation: null };
  }
  return { access: NO_ACCESS, impersonation: stored };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isInitializing, setIsInitializing] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<BambooUser | null>(null);
  const [hasBambooAccess, setHasBambooAccess] = useState<boolean | null>(null);
  const [district, setDistrict] = useState<BambooDistrict | null>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isDistrictAdmin, setIsDistrictAdmin] = useState(false);
  const [features, setFeatures] = useState<Record<string, boolean>>({});
  const [impersonation, setImpersonation] = useState<ImpersonationTarget | null>(null);
  const [activeSource, setActiveSource] = useState<SessionSource>(null);
  const refreshGeneration = useRef(0);
  const sessionEpoch = useRef(0);

  const refresh = useCallback(async ({ follow, failClosed = false }: RefreshOptions = {}) => {
    const generation = ++refreshGeneration.current;
    setHasBambooAccess(null);
    setDistrict(null);
    setIsSuperAdmin(false);
    setIsDistrictAdmin(false);
    setFeatures({});
    try {
      if (failClosed) {
        clearHandoff();
        clearSession();
        throw new Error('Handoff refused');
      }
      const { source, sub } = await resolveSessionSource();
      if (generation !== refreshGeneration.current) return;
      if (!source) {
        throw new Error('No active session');
      }
      claimDistrictCaches(sub);
      if (source === 'handoff' && follow !== undefined) {
        const followed = await followHandoffImitation(follow, getHandoffToken() ?? '');
        if (generation !== refreshGeneration.current) {
          if (followed) abandonImpersonation();
          return;
        }
        if (follow !== null && !followed) {
          refuseHandoff();
          throw new Error('Requested imitation could not be followed');
        }
      }
      const required = source === 'handoff' ? handoffImitating() : null;
      if (required !== null && !impersonationMatches(required)) {
        refuseHandoff();
        throw new Error('Requested imitation is not in place');
      }
      setSessionSource(source);
      setActiveSource(source);
      setIsAuthenticated(true);
      const { access, impersonation: target } = await fetchAccessForSession();
      if (generation !== refreshGeneration.current) return;
      if (required !== null && !(access.hasAccess && access.impersonating && target !== null)) {
        refuseHandoff();
        throw new Error('Requested imitation was refused');
      }
      // Refreshed before the access state is published, not after: publishing
      // first opens BambooGate, and consumers read loadThresholds() once at
      // mount, so they would hold the stale values for the rest of the session.
      if (access.hasAccess) await Promise.all([hydrateThresholds(), hydrateSchoolYears()]);
      if (generation !== refreshGeneration.current) return;
      if (access.hasAccess) rememberPlanCriteria(access.districtStateName);
      if (source === 'handoff' && access.hasAccess) {
        const epoch = sessionEpoch.current;
        recordHandoffSignIn(epoch, () => epoch === sessionEpoch.current);
      }
      setHasBambooAccess(access.hasAccess);
      setUser(access.userId ? { id: access.userId, first_name: null } : null);
      setDistrict({
        name: access.districtName,
        state: access.districtState,
        stateName: access.districtStateName,
      });
      setIsSuperAdmin(access.isSuperAdmin);
      setIsDistrictAdmin(access.isDistrictAdmin);
      setFeatures(access.features);
      setImpersonation(target);
    } catch {
      if (generation !== refreshGeneration.current) return;
      setSessionSource(null);
      setActiveSource(null);
      abandonImpersonation();
      setIsAuthenticated(false);
      setUser(null);
      setHasBambooAccess(false);
      setDistrict(null);
      setIsSuperAdmin(false);
      setIsDistrictAdmin(false);
      setFeatures({});
      setImpersonation(null);
    }
  }, []);

  const refreshUser = useCallback(() => refresh(), [refresh]);

  const forgetSession = useCallback(() => {
    refreshGeneration.current += 1;
    sessionEpoch.current += 1;
    setSessionSource(null);
    setActiveSource(null);
    clearDistrictCaches();
    setIsAuthenticated(false);
    setUser(null);
    setHasBambooAccess(null);
    setDistrict(null);
    setIsSuperAdmin(false);
    setIsDistrictAdmin(false);
    setFeatures({});
    setImpersonation(null);
  }, []);

  const signOut = useCallback(async () => {
    const handedOff = getSessionSource() === 'handoff';
    try {
      if (!handedOff) await amplifySignOut();
    } finally {
      clearHandoff();
      clearSession();
      forgetSession();
    }
  }, [forgetSession]);

  useEffect(
    () =>
      subscribeSessionChange((_change, of) => {
        if (getSessionSource() !== of) return;
        refreshGeneration.current += 1;
        clearHandoff();
        clearSession();
        abandonImpersonation();
        forgetSession();
      }),
    [forgetSession]
  );

  useEffect(() => {
    let active = true;
    (async () => {
      const generation = ++refreshGeneration.current;
      const handoff = await bootstrapHandoffFromUrl();
      if (generation !== refreshGeneration.current) {
        if (active && handoff.status === 'signed-in') {
          clearHandoff();
          forgetHandoffTab();
        }
      } else {
        await refresh(
          handoff.status === 'signed-in' ? { follow: handoff.imitating }
          : handoff.status === 'failed' ? { failClosed: true }
          : {}
        );
      }
      if (active) setIsInitializing(false);
    })();
    return () => {
      active = false;
    };
  }, [refresh]);

  useEffect(() => {
    if (!isAuthenticated || activeSource !== 'handoff') return undefined;
    const timer = setInterval(() => {
      if (getSessionSource() === 'handoff' && !getHandoffToken()) notifySessionChange('lost', 'handoff');
    }, HANDOFF_CHECK_MS);
    return () => clearInterval(timer);
  }, [isAuthenticated, activeSource]);

  useEffect(() => installBackForwardGuard(), []);

  useEffect(() => installCrossTabSync(), []);

  useEffect(
    () =>
      registerImpersonationCleanup(clearDistrictCaches),
    []
  );

  useEffect(() => {
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signedIn') {
        setSessionSource(null);
        clearHandoff();
        abandonImpersonation();
        forgetHandoffTab();
        refreshUser();
      } else if (payload.event === 'signedOut') {
        clearSession();
        forgetSession();
      }
    });
    return () => {
      unsubscribe();
    };
  }, [refreshUser, forgetSession]);

  const hasFeature = useCallback((key: string) => features[key] === true, [features]);

  const value = useMemo(
    () => ({
      isInitializing,
      isAuthenticated,
      user,
      hasBambooAccess,
      district,
      isSuperAdmin,
      isDistrictAdmin,
      hasFeature,
      impersonation,
      refreshUser,
      signOut,
    }),
    [
      isInitializing,
      isAuthenticated,
      user,
      hasBambooAccess,
      district,
      isSuperAdmin,
      isDistrictAdmin,
      hasFeature,
      impersonation,
      refreshUser,
      signOut,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}
